import React from "react";
import {
  IonContent,
  IonHeader,
  IonToolbar,
  IonModal,
  IonButton,
  IonButtons,
} from "@ionic/react";

import { Message } from "../../interfaces";

import "./media-modal.css";
import { InteractiveData } from "../../interfaces";
import { FormattedMessage } from "react-intl";

function InteractiveDownload({
  content,
}: {
  content: InteractiveData | undefined;
}): JSX.Element {
  const [videoError, setVideoError] = React.useState(false);
  if (!content) return <></>;
  const component: { [key: string]: React.ReactNode | JSX.Element } =
    React.useMemo(
      () => ({
        audio: content.header?.audio?.url ? (
          <div className="interactive-audio">
            <audio controls>
              <source
                src={`https://s3.visitoai.com/${content.header.audio.url}`}
              />
              Your browser does not support the audio element.
            </audio>
          </div>
        ) : (
          <span>voice note</span>
        ),
        document: (
          <a
            target="_blank"
            href={`https://s3.visitoai.com/${content.header?.document?.url}`}
            download
          >
            <FormattedMessage id="common.download" />
          </a>
        ),
        file: (
          <div className="interactive-file flex flex-column gap-1 mb-1">
            <a
              href={content.header?.file?.url}
              target="_blank"
              rel="noreferrer"
              className="flex align-items-center"
            ></a>
          </div>
        ),
        video: (
          <div className="interactive-video">
            {!videoError ? (
              <video autoPlay={true} muted={true}>
                <source
                  src={
                    content.header?.video?.url?.includes("https://")
                      ? content.header?.video?.url
                      : `https://s3.visitoai.com/${content.header?.video?.url}`
                  }
                  type={content.header?.video?.mimeType}
                  onError={() => setVideoError(true)}
                  style={{ width: "100%", height: "auto" }}
                />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src="https://placehold.co/200x200?text=Content+no+longer+available"
                alt="Video failed to load"
              />
            )}
          </div>
        ),
        image: (
          <a
            target="_blank"
            href={
              content.header?.image?.url?.includes("https://")
                ? content.header?.image?.url
                : `https://s3.visitoai.com/${content.header?.image?.url}`
            }
            download
          >
            <FormattedMessage id="common.download" />
          </a>
        ),
      }),
      [content.header]
    );

  return (component[content.header.type] as JSX.Element) || <></>;
}

function InteractiveContent({
  content,
}: {
  content: InteractiveData | undefined;
}): JSX.Element {
  const [videoError, setVideoError] = React.useState(false);
  if (!content) return <></>;
  const component: { [key: string]: React.ReactNode | JSX.Element } =
    React.useMemo(
      () => ({
        audio: content.header?.audio?.url ? (
          <div className="interactive-audio">
            <audio controls>
              <source
                src={`https://s3.visitoai.com/${content.header.audio.url}`}
              />
              Your browser does not support the audio element.
            </audio>
          </div>
        ) : (
          <span>voice note</span>
        ),
        document: (() => {
          const url = `https://s3.visitoai.com/${content.header?.document?.url}`;
          const extension = url.split(".").pop()?.toLowerCase();

          if (extension === "zip") {
            return <div>Zip File</div>;
          }

          const isPdf = extension === "pdf";

          return (
            <>
              {isPdf ? (
                <iframe
                  src={url}
                  title="Document file PDF"
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              ) : (
                <iframe
                  title="Document file DOCX"
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(
                    url
                  )}&embedded=true`}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              )}
            </>
          );
        })(),
        file: (
          <div className="interactive-file flex flex-column gap-1 mb-1">
            <a
              href={content.header?.file?.url}
              target="_blank"
              rel="noreferrer"
              className="flex align-items-center"
            ></a>
          </div>
        ),
        image: (
          <>
            <img
              alt=""
              src={
                content.header?.image?.url?.includes("https://")
                  ? content.header?.image?.url
                  : `https://s3.visitoai.com/${content.header?.image?.url}`
              }
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // prevents looping
                currentTarget.src =
                  "https://placehold.co/200x200?text=Content+no+longer+available";
              }}
            />
          </>
        ),
      }),
      [content.header]
    );

  return (component[content.header.type] as JSX.Element) || <></>;
}

const MediaModal: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  if (!props.message) return null;
  const { isOpen, message, setIsOpen } = props;
  function onWillDismiss() {
    setIsOpen(false);
  }
  return (
    <IonModal
      isOpen={isOpen}
      className="pictures-modal"
      onWillDismiss={() => onWillDismiss()}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => setIsOpen(false)}>
              <FormattedMessage id="common.close" />
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <InteractiveDownload content={message?.interactive?.data} />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="modal-media-container">
          <InteractiveContent content={message?.interactive?.data} />
        </div>
      </IonContent>
    </IonModal>
  );
};

interface ILayoutProps {
  isOpen: boolean;
  setIsOpen: Function;
  message: Message | null;
}

export default MediaModal;
